import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { trackEvent } from "@/components/marketing/Layout";
import { usePlans } from "@/components/marketing/Plans";
import { brl } from "@/lib/format";
import { SEGMENTS } from "@/lib/marketing";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Auth({ mode = "login" }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const auth = useAuth();
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({ name: "", email: "", password: "", restaurant_name: "", phone: "", plan: params.get("plan") || "essencial", ref: params.get("ref") || localStorage.getItem("pg_ref") || "", business_type: params.get("business") || "" });
  const [loading, setLoading] = useState(false);
  const plans = usePlans();
  useEffect(() => { if (params.get("ref")) localStorage.setItem("pg_ref", params.get("ref")); if (mode === "signup") trackEvent("signup_started"); }, [params, mode]);

  const doLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await auth.login(form.email, form.password); nav("/app"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro no login"); }
    setLoading(false);
  };
  const doSignup = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await auth.signup({ ...form, ref: form.ref || null, business_type: form.business_type || null });
      localStorage.removeItem("pg_ref");
      toast.success(`Conta criada! Teste grátis por 14 dias no plano ${r.restaurant.plan}.`);
      nav("/app/onboarding");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no cadastro"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-stone-950">
        <img src="https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwa2l0Y2hlbiUyMGNoZWYlMjBjb29raW5nfGVufDB8fHx8MTc5MDAxMjM0M3ww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-stone-950 via-stone-950/70 to-transparent" />
        <div className="absolute top-8 left-8"><Link to="/"><BrandLogo tone="dark" /></Link></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-display text-4xl font-extrabold leading-tight">Gestão de verdade,<br />para restaurantes de verdade.</h2>
          <p className="mt-3 text-stone-300 max-w-md">Do primeiro pedido ao fechamento do caixa — em um sistema só.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8"><Link to="/"><BrandLogo /></Link></div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-8">
              <TabsTrigger value="login" data-testid="tab-login">Entrar</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Criar conta</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <h1 className="font-display text-3xl font-extrabold text-stone-900">Bem-vindo de volta.</h1>
              <p className="text-stone-500 mt-1">Entre na sua conta para acessar seu painel.</p>
              <form onSubmit={doLogin} className="mt-8 space-y-4">
                <div><Label>E-mail</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" /></div>
                <div><Label>Senha</Label><Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" /></div>
                <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="login-submit">
                  {loading ? "Entrando..." : "Entrar"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <h1 className="font-display text-3xl font-extrabold text-stone-900">Comece grátis.</h1>
              <p className="text-stone-500 mt-1">14 dias no plano {form.plan}. Sem cartão.</p>
              <form onSubmit={doSignup} className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Seu nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" /></div>
                  <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="signup-phone" /></div>
                </div>
                <div><Label>Nome do restaurante</Label><Input required value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })} data-testid="signup-restaurant" /></div>
                <div><Label>E-mail</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" /></div>
                <div><Label>Senha (mínimo 6 caracteres)</Label><Input type="password" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password" /></div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de negócio</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} data-testid="signup-business">
                    <option value="">Selecione</option>{Object.entries(SEGMENTS).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Plano</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="signup-plan">
                    {plans.length === 0 && <option value={form.plan}>Carregando...</option>}
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.name}{p.price > 0 ? ` — ${brl(p.price)}` : ""}</option>)}
                  </select>
                </div>
                </div>
                {form.ref && <p className="text-xs text-emerald-700" data-testid="signup-ref">Indicado por: {form.ref}</p>}
                <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="signup-submit">
                  {loading ? "Criando..." : "Criar conta grátis"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <div className="mt-6 text-center">
            <Link to="/admin-master/login" className="text-xs text-stone-400 hover:text-stone-700" data-testid="auth-admin-link">Acesso Admin Master →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
